import {
  ChatProcessStepStatus,
  ChatProcessStepType,
  ChatToolCallState,
  ChatToolProvider,
} from '@/modules/common/types';
import type { SendMessageMutation } from '@/modules/common/types';

import { WEB_SEARCH_TOOL_NAME } from './chat-tool-registry';

export type ChatMessage = NonNullable<SendMessageMutation['sendMessage']['messages']>[number];
export type ChatToolCall = ChatMessage['toolCalls'][number];
export type ChatProcessStep = ChatMessage['processSteps'][number];
export type ChatMessageWithProcessSteps = ChatMessage & { processSteps?: ChatProcessStep[] };
export type ProcessStepNode = ChatProcessStep & { children: ProcessStepNode[] };

export const TOOL_PROVIDER_TITLE: Record<ChatToolProvider, string> = {
  [ChatToolProvider.MAVUE]: 'Used Mavue',
  [ChatToolProvider.WEB_SEARCH]: 'Used Web Search',
};

export const TOOL_CALL_STATE_PROCESS_STATUS: Record<ChatToolCallState, ChatProcessStepStatus> = {
  [ChatToolCallState.INPUT_AVAILABLE]: ChatProcessStepStatus.RUNNING,
  [ChatToolCallState.APPROVAL_REQUESTED]: ChatProcessStepStatus.AWAITING_APPROVAL,
  [ChatToolCallState.APPROVAL_RESPONDED]: ChatProcessStepStatus.PENDING,
  [ChatToolCallState.OUTPUT_AVAILABLE]: ChatProcessStepStatus.COMPLETED,
  [ChatToolCallState.OUTPUT_ERROR]: ChatProcessStepStatus.ERROR,
  [ChatToolCallState.OUTPUT_DENIED]: ChatProcessStepStatus.DENIED,
};

export const TERMINAL_PROCESS_STEP_STATUSES = new Set<ChatProcessStepStatus>([
  ChatProcessStepStatus.COMPLETED,
  ChatProcessStepStatus.ERROR,
  ChatProcessStepStatus.DENIED,
]);

const PROVIDER_STATUS_PRIORITY = [
  ChatProcessStepStatus.AWAITING_APPROVAL,
  ChatProcessStepStatus.RUNNING,
  ChatProcessStepStatus.PENDING,
  ChatProcessStepStatus.ERROR,
  ChatProcessStepStatus.DENIED,
] as const;

export function getToolCallProvider(call: ChatToolCall) {
  return call.toolName === WEB_SEARCH_TOOL_NAME
    ? ChatToolProvider.WEB_SEARCH
    : ChatToolProvider.MAVUE;
}

export function normalizeToolProvider(
  provider: ChatProcessStep['provider'] | string | null | undefined,
  title?: string | null
) {
  if (provider === ChatToolProvider.WEB_SEARCH || provider === 'web_search') {
    return ChatToolProvider.WEB_SEARCH;
  }

  if (provider === ChatToolProvider.MAVUE || provider === 'mavue') {
    return ChatToolProvider.MAVUE;
  }

  if (title === TOOL_PROVIDER_TITLE[ChatToolProvider.WEB_SEARCH]) {
    return ChatToolProvider.WEB_SEARCH;
  }

  if (title === TOOL_PROVIDER_TITLE[ChatToolProvider.MAVUE]) {
    return ChatToolProvider.MAVUE;
  }

  return null;
}

export function getProviderTitle(provider: ChatToolProvider | null, fallbackTitle?: string | null) {
  return provider ? TOOL_PROVIDER_TITLE[provider] : fallbackTitle?.trim() || 'Used Tools';
}

export function getProviderStatusFromChildren(children: ChatProcessStep[]) {
  return (
    PROVIDER_STATUS_PRIORITY.find((status) => children.some((step) => step.status === status)) ??
    ChatProcessStepStatus.COMPLETED
  );
}

export function isTerminalProcessStepStatus(status: ChatProcessStepStatus) {
  return TERMINAL_PROCESS_STEP_STATUSES.has(status);
}

export function isActiveStep(step: ChatProcessStep) {
  return (
    step.status === ChatProcessStepStatus.RUNNING ||
    step.status === ChatProcessStepStatus.AWAITING_APPROVAL ||
    step.status === ChatProcessStepStatus.PENDING
  );
}

export function getStepTime(value: string | Date | null | undefined) {
  if (!value) {
    return null;
  }

  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

export function formatWorkDuration(milliseconds: number) {
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));

  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

export function getWorkLogDurationRange({
  message,
  processSteps,
  isLive,
}: {
  message: ChatMessageWithProcessSteps;
  processSteps: ChatProcessStep[];
  isLive: boolean;
}) {
  const startedAt =
    processSteps
      .map((step) => getStepTime(step.startedAt))
      .find((time): time is number => !!time) ??
    getStepTime(message.createdAt) ??
    Date.now();
  const completedTimes = processSteps
    .map((step) => getStepTime(step.completedAt))
    .filter((time): time is number => !!time);
  const endedAt = isLive
    ? null
    : completedTimes.length > 0
      ? Math.max(...completedTimes)
      : Date.now();

  return { startedAt, endedAt };
}

export function getWorkLogDurationFromRange({
  startedAt,
  endedAt,
  now = Date.now(),
}: {
  startedAt: number;
  endedAt: number | null;
  now?: number;
}) {
  return formatWorkDuration((endedAt ?? now) - startedAt);
}

function isWorkLogProcessStep(step: ChatProcessStep) {
  if (
    step.type === ChatProcessStepType.TOOL_PROVIDER ||
    step.type === ChatProcessStepType.TOOL_CALL
  ) {
    return true;
  }

  return step.type === ChatProcessStepType.PROCESS_SUMMARY && !!step.parentId;
}

function getProviderCompletedAt({
  children,
  fallbackCompletedAt,
  status,
}: {
  children: ChatProcessStep[];
  fallbackCompletedAt?: string | Date | null;
  status: ChatProcessStepStatus;
}) {
  if (!isTerminalProcessStepStatus(status)) {
    return null;
  }

  let latestValue: string | Date | null = fallbackCompletedAt ?? null;
  let latestTime = getStepTime(latestValue);

  for (const child of children) {
    const value = child.completedAt ?? child.updatedAt;
    const time = getStepTime(value);

    if (time !== null && (latestTime === null || time > latestTime)) {
      latestTime = time;
      latestValue = value;
    }
  }

  return latestValue;
}

function getChildStepProvider({
  step,
  providerStepsById,
  toolCallsById,
}: {
  step: ChatProcessStep;
  providerStepsById: Map<string, ChatProcessStep>;
  toolCallsById: Map<string, ChatToolCall>;
}) {
  const parentProvider = step.parentId ? providerStepsById.get(step.parentId) : null;
  const linkedToolCall = step.chatToolCallId ? toolCallsById.get(step.chatToolCallId) : null;

  return (
    normalizeToolProvider(step.provider) ??
    normalizeToolProvider(parentProvider?.provider, parentProvider?.title) ??
    (linkedToolCall ? getToolCallProvider(linkedToolCall) : null)
  );
}

function createSyntheticProviderStep({
  id,
  messageId,
  provider,
  templateStep,
}: {
  id: string;
  messageId: string;
  provider: ChatToolProvider | null;
  templateStep: ChatProcessStep;
}): ChatProcessStep {
  return {
    id,
    messageId,
    parentId: null,
    chatToolCallId: null,
    type: ChatProcessStepType.TOOL_PROVIDER,
    status: templateStep.status,
    title: getProviderTitle(provider),
    summary: 'Tools used by the assistant.',
    provider,
    metadata: provider ? { provider } : null,
    orderIndex: templateStep.orderIndex,
    startedAt: templateStep.startedAt,
    completedAt: templateStep.completedAt,
    createdAt: templateStep.createdAt,
    updatedAt: templateStep.updatedAt,
  };
}

export function normalizeWorkLogProcessSteps({
  messageId,
  processSteps,
  toolCalls,
}: {
  messageId: string;
  processSteps: ChatProcessStep[];
  toolCalls: ChatToolCall[];
}) {
  const sortedSteps = [...processSteps].sort((a, b) => a.orderIndex - b.orderIndex);
  const providerStepsById = new Map(
    sortedSteps
      .filter((step) => step.type === ChatProcessStepType.TOOL_PROVIDER)
      .map((step) => [step.id, step])
  );
  const toolCallsById = new Map(toolCalls.map((call) => [call.id, call]));
  const childSteps = sortedSteps.filter(
    (step) =>
      step.type === ChatProcessStepType.TOOL_CALL ||
      (step.type === ChatProcessStepType.PROCESS_SUMMARY && !!step.parentId)
  );

  if (childSteps.length === 0) {
    return sortedSteps;
  }

  const usedProviderStepIds = new Set<string>();
  const runs: Array<{
    provider: ChatToolProvider | null;
    providerStep: ChatProcessStep;
    children: ChatProcessStep[];
  }> = [];

  for (const step of childSteps) {
    const provider = getChildStepProvider({ step, providerStepsById, toolCallsById });
    const parentProvider = step.parentId ? providerStepsById.get(step.parentId) : null;
    const latestRun = runs[runs.length - 1];

    if (!latestRun || latestRun.provider !== provider) {
      const baseProviderId = parentProvider?.id ?? `${messageId}-${provider ?? 'tools'}`;
      const providerStepId = usedProviderStepIds.has(baseProviderId)
        ? `${baseProviderId}-run-${runs.length}`
        : baseProviderId;
      const providerStep = parentProvider
        ? {
            ...parentProvider,
            id: providerStepId,
            provider,
            title: getProviderTitle(provider, parentProvider.title),
          }
        : createSyntheticProviderStep({
            id: providerStepId,
            messageId,
            provider,
            templateStep: step,
          });

      usedProviderStepIds.add(providerStepId);
      runs.push({ provider, providerStep, children: [] });
    }

    const run = runs[runs.length - 1];
    run.children.push({
      ...step,
      parentId: run.providerStep.id,
      provider,
    });
  }

  const normalizedSteps: ChatProcessStep[] = [];
  let nextOrderIndex = 0;

  for (const run of runs) {
    const children = [...run.children].sort((a, b) => a.orderIndex - b.orderIndex);
    const firstChild = children[0];
    const status = getProviderStatusFromChildren(children);

    normalizedSteps.push({
      ...run.providerStep,
      status,
      orderIndex: nextOrderIndex,
      startedAt: firstChild?.startedAt ?? run.providerStep.startedAt,
      completedAt: getProviderCompletedAt({
        children,
        fallbackCompletedAt: run.providerStep.completedAt,
        status,
      }),
    });
    nextOrderIndex += 1;

    for (const child of children) {
      normalizedSteps.push({
        ...child,
        orderIndex: nextOrderIndex,
      });
      nextOrderIndex += 1;
    }
  }

  return normalizedSteps;
}

export function getFallbackProcessSteps({
  messageId,
  toolCalls,
}: {
  messageId: string;
  toolCalls: ChatToolCall[];
}): ChatProcessStep[] {
  if (toolCalls.length === 0) {
    return [];
  }

  const providerRuns: Array<{ provider: ChatToolProvider; toolCalls: ChatToolCall[] }> = [];
  const steps: ChatProcessStep[] = [];
  let orderIndex = 0;
  const createdAt = new Date().toISOString();

  for (const call of toolCalls) {
    const provider = getToolCallProvider(call);
    const latestRun = providerRuns[providerRuns.length - 1];

    if (latestRun?.provider === provider) {
      latestRun.toolCalls.push(call);
    } else {
      providerRuns.push({ provider, toolCalls: [call] });
    }
  }

  for (const [runIndex, run] of providerRuns.entries()) {
    const providerStepId = `${messageId}-${run.provider}-${runIndex}`;
    const childSteps = run.toolCalls.map(
      (call): ChatProcessStep => ({
        id: `${messageId}-${call.toolCallId}`,
        messageId,
        parentId: providerStepId,
        chatToolCallId: call.id,
        type: ChatProcessStepType.TOOL_CALL,
        status: TOOL_CALL_STATE_PROCESS_STATUS[call.state],
        title: call.title || call.toolName,
        provider: run.provider,
        metadata: null,
        orderIndex: 0,
        startedAt: call.createdAt,
        completedAt: call.updatedAt,
        createdAt,
        updatedAt: createdAt,
      })
    );
    const providerStatus = getProviderStatusFromChildren(childSteps);

    steps.push({
      id: providerStepId,
      messageId,
      parentId: null,
      chatToolCallId: null,
      type: ChatProcessStepType.TOOL_PROVIDER,
      status: providerStatus,
      title: getProviderTitle(run.provider),
      summary: null,
      provider: run.provider,
      metadata: null,
      orderIndex,
      startedAt: childSteps[0]?.startedAt ?? createdAt,
      completedAt: getProviderCompletedAt({
        children: childSteps,
        fallbackCompletedAt: childSteps[childSteps.length - 1]?.completedAt,
        status: providerStatus,
      }),
      createdAt,
      updatedAt: createdAt,
    });
    orderIndex += 1;

    for (const childStep of childSteps) {
      steps.push({
        ...childStep,
        orderIndex,
      });
      orderIndex += 1;
    }
  }

  return steps;
}

export function getWorkLogProcessSteps({
  message,
  toolCalls,
}: {
  message: ChatMessageWithProcessSteps;
  toolCalls: ChatToolCall[];
}) {
  const persistedSteps = message.processSteps?.filter(isWorkLogProcessStep) ?? [];

  return persistedSteps.length > 0
    ? normalizeWorkLogProcessSteps({
        messageId: message.id,
        processSteps: persistedSteps,
        toolCalls,
      })
    : getFallbackProcessSteps({ messageId: message.id, toolCalls });
}

function getProcessStepMetadata(step: ChatProcessStep): Record<string, unknown> {
  return step.metadata && typeof step.metadata === 'object' && !Array.isArray(step.metadata)
    ? (step.metadata as Record<string, unknown>)
    : {};
}

export function getActiveApprovalCall({
  toolCalls,
  processSteps,
}: {
  toolCalls: ChatToolCall[];
  processSteps: ChatProcessStep[];
}) {
  const activeStep = processSteps.find((step) => {
    const metadata = getProcessStepMetadata(step);

    return metadata.approvalActive === true && step.chatToolCallId;
  });

  if (activeStep?.chatToolCallId) {
    const activeCall = toolCalls.find(
      (call) =>
        call.id === activeStep.chatToolCallId && call.state === ChatToolCallState.APPROVAL_REQUESTED
    );

    if (activeCall) {
      return activeCall;
    }
  }

  return toolCalls.find((call) => call.state === ChatToolCallState.APPROVAL_REQUESTED) ?? null;
}

export function nodeContainsActiveApproval(
  node: ProcessStepNode,
  activeApprovalCallId: string | null
): boolean {
  if (!activeApprovalCallId) {
    return false;
  }

  if (node.chatToolCallId === activeApprovalCallId) {
    return true;
  }

  return node.children.some((child) => nodeContainsActiveApproval(child, activeApprovalCallId));
}

function shouldHideQueuedApprovalNode({
  node,
  toolCallsById,
  activeApprovalCallId,
}: {
  node: ProcessStepNode;
  toolCallsById: Map<string, ChatToolCall>;
  activeApprovalCallId: string | null;
}) {
  if (!node.chatToolCallId) {
    return false;
  }

  const toolCall = toolCallsById.get(node.chatToolCallId);

  return (
    toolCall?.state === ChatToolCallState.APPROVAL_REQUESTED && toolCall.id !== activeApprovalCallId
  );
}

export function getVisibleProcessStepTree({
  nodes,
  toolCallsById,
  activeApprovalCallId,
}: {
  nodes: ProcessStepNode[];
  toolCallsById: Map<string, ChatToolCall>;
  activeApprovalCallId: string | null;
}): ProcessStepNode[] {
  return nodes.flatMap((node) => {
    if (shouldHideQueuedApprovalNode({ node, toolCallsById, activeApprovalCallId })) {
      return [];
    }

    return [
      {
        ...node,
        children: getVisibleProcessStepTree({
          nodes: node.children,
          toolCallsById,
          activeApprovalCallId,
        }),
      },
    ];
  });
}

export function buildProcessStepTree(processSteps: ChatProcessStep[]) {
  const nodesById = new Map<string, ProcessStepNode>();

  for (const step of [...processSteps].sort((a, b) => a.orderIndex - b.orderIndex)) {
    nodesById.set(step.id, { ...step, children: [] });
  }

  const roots: ProcessStepNode[] = [];

  for (const node of nodesById.values()) {
    if (node.parentId && nodesById.has(node.parentId)) {
      nodesById.get(node.parentId)?.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots.filter(isWorkLogProcessStep);
}
